const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api/v1";
const STORAGE_KEY = "wasaa_partner_portal_tokens";

const WRONG_ROLE_MESSAGE = "This page belongs to a different role. Use the role switcher to change role.";

let tokens = readStoredTokens();
const listeners = new Set();
const wrongRoleListeners = new Set();
const suspendedListeners = new Set();

function readStoredTokens() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function persistTokens(next) {
  tokens = next;
  try {
    if (next) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // localStorage unavailable (private browsing, etc.) — the session still works for
    // this page load, it just won't survive a refresh.
  }
  listeners.forEach((listener) => listener(tokens));
}

export function onAuthChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function onWrongRole(listener) {
  wrongRoleListeners.add(listener);
  return () => wrongRoleListeners.delete(listener);
}

export function onAccessSuspended(listener) {
  suspendedListeners.add(listener);
  return () => suspendedListeners.delete(listener);
}

export function getTokens() {
  return tokens;
}

export class ApiError extends Error {
  constructor(message, status, code) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

async function parseErrorEnvelope(response) {
  try {
    const body = await response.json();
    const message = body?.error?.message || response.statusText;
    // A raw Zod validation error (e.g. "body/email Invalid email, body/password
    // String must contain at least 1 character(s)") is an implementation detail of
    // the request schema, not something a user should ever see verbatim.
    const friendly = /^body\//.test(message) ? "Please check your input and try again." : message;
    return { message: friendly, code: body?.error?.code || "UNKNOWN" };
  } catch {
    return { message: response.statusText, code: "UNKNOWN" };
  }
}

async function rawRequest(path, options, accessToken) {
  const headers = { ...(options.headers || {}) };
  if (options.body !== undefined && !(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  return fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
    body: options.body !== undefined && !(options.body instanceof FormData) ? JSON.stringify(options.body) : options.body,
  });
}

const CHALLENGE_ERROR_CODES = new Set(["MFA_CHALLENGE_INVALID", "MFA_CODE_INVALID", "MFA_CODE_EXPIRED", "MFA_TOO_MANY_ATTEMPTS"]);

async function isChallengeError(response) {
  try {
    const body = await response.clone().json();
    return CHALLENGE_ERROR_CODES.has(body?.error?.code);
  } catch {
    return false;
  }
}

let refreshInFlight = null;

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== STORAGE_KEY) return;
    tokens = readStoredTokens();
    listeners.forEach((listener) => listener(tokens));
  });
}

async function performRefresh(refreshToken) {
  let res;
  try {
    res = await rawRequest("/auth/refresh", { method: "POST", body: { refreshToken } });
  } catch {
    return false;
  }
  if (!res.ok) {
    const latest = readStoredTokens();
    if (latest?.refreshToken && latest.refreshToken !== refreshToken) {
      tokens = latest;
      return true;
    }
    if (res.status === 401) persistTokens(null);
    return false;
  }
  const body = await res.json();
  persistTokens({ ...(tokens || {}), accessToken: body.accessToken, refreshToken: body.refreshToken });
  return true;
}

function tryRefresh() {
  const stored = readStoredTokens();
  if (stored?.refreshToken && stored.refreshToken !== tokens?.refreshToken) {
    tokens = stored;
    return Promise.resolve(true);
  }
  if (!tokens?.refreshToken) return Promise.resolve(false);
  if (!refreshInFlight) {
    refreshInFlight = performRefresh(tokens.refreshToken).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export async function apiRequest(path, options = {}) {
  let response = await rawRequest(path, options, tokens?.accessToken);

  if (response.status === 401 && tokens?.refreshToken && !options.__isRetry && !(await isChallengeError(response))) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      response = await rawRequest(path, options, tokens?.accessToken);
    }
  }

  if (!response.ok) {
    const { message, code } = await parseErrorEnvelope(response);
    if (response.status === 401 && !CHALLENGE_ERROR_CODES.has(code)) persistTokens(null);
    if (code === "PASSWORD_CHANGE_REQUIRED" && !window.location.pathname.startsWith("/auth/set-password")) {
      window.location.assign("/auth/set-password");
    }
    if (response.status === 403 && (code === "TENANT_SUSPENDED" || code === "PARTNER_ACCESS_SUSPENDED") && path !== "/portal/context" && path !== "/portal/session/switch") {
      suspendedListeners.forEach((listener) => listener(code));
    }
    if (code === "WRONG_ROLE") {
      wrongRoleListeners.forEach((listener) => listener(WRONG_ROLE_MESSAGE));
      throw new ApiError(WRONG_ROLE_MESSAGE, response.status, code);
    }
    throw new ApiError(message, response.status, code);
  }

  if (response.status === 204) return null;
  return response.json();
}

export async function publicPost(path, body) {
  const response = await rawRequest(path, { method: "POST", body }, null);
  if (!response.ok) {
    const { message, code } = await parseErrorEnvelope(response);
    throw new ApiError(message, response.status, code);
  }
  return response.status === 204 ? null : response.json();
}

export async function publicGet(path) {
  const response = await rawRequest(path, { method: "GET" }, null);
  if (!response.ok) {
    const { message, code } = await parseErrorEnvelope(response);
    throw new ApiError(message, response.status, code);
  }
  return response.json();
}

export function apiGet(path) {
  return apiRequest(path, { method: "GET" });
}

export function apiPost(path, body) {
  return apiRequest(path, { method: "POST", body });
}

export function apiPatch(path, body) {
  return apiRequest(path, { method: "PATCH", body });
}

export function apiDelete(path) {
  return apiRequest(path, { method: "DELETE" });
}

export async function login(email, password, seat) {
  const payload = { email, password };
  if (seat) payload.seat = seat;
  const body = await publicPost("/portal/auth/login", payload);
  if (body?.mfaRequired) {
    return {
      mfaRequired: true,
      challengeToken: body.challengeToken,
      channel: body.channel,
      destinationHint: body.destinationHint,
      expiresAt: body.expiresAt,
    };
  }
  persistTokens({ accessToken: body.accessToken, refreshToken: body.refreshToken });
  return { user: body.user, mustChangePassword: Boolean(body.mustChangePassword) };
}

export async function verifyMfa(challengeToken, code) {
  const body = await publicPost("/auth/mfa/verify", { challengeToken, code });
  persistTokens({ accessToken: body.accessToken, refreshToken: body.refreshToken });
  return { user: body.user, mustChangePassword: Boolean(body.mustChangePassword) };
}

export async function switchSeat(seat) {
  const body = await apiRequest("/portal/session/switch", { method: "POST", body: seat });
  persistTokens({ accessToken: body.accessToken, refreshToken: body.refreshToken });
  return { user: body.user, mustChangePassword: Boolean(body.mustChangePassword) };
}

export function resendMfa(challengeToken) {
  return publicPost("/auth/mfa/resend", { challengeToken });
}

export function apiPut(path, body) {
  return apiRequest(path, { method: "PUT", body });
}

export function logout() {
  const accessToken = tokens?.accessToken;
  persistTokens(null);
  // Best-effort: revoke the session server-side too, using the token we just cleared
  // locally. The user is signed out locally either way, so a failure here (network,
  // already-expired token) is not worth surfacing.
  if (accessToken) {
    rawRequest("/auth/logout", { method: "POST" }, accessToken).catch(() => {});
  }
}

export function buildQueryString(params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") search.set(key, value);
  });
  const query = search.toString();
  return query ? `?${query}` : "";
}
